import {
  createContext, useContext, useRef, useState, useCallback, useEffect, ReactNode,
} from 'react';
import { getIceServers, logCall, logCallEvent, logCallKeepalive, CallLogCreate, CallOutcome } from '../api/calls';
import { setCallActive, isOtherCallActive } from '../utils/call/callBusy';
import { deriveOutcome } from '../utils/call/callLog';
import { SpeakingMonitor } from '../utils/call/speaking';
import { bumpMissed } from '../utils/call/missedBadge';
import { getConversation } from '../api/chat';
import { PeerService } from '../webrtc/PeerService';
import CallScreen from '../components/call/CallScreen';
import StartCallPicker from '../components/calls/StartCallPicker';
import {
  startRingtone, startRingback, stopAllCallSounds, playConnectTone, playEndTone,
  setSystemRingtone,
} from '../utils/call/callSounds';
import {
  getSystemRingtone, showCallNotification, closeCallNotification,
  onCallNotificationAction, showDesktopNotification,
} from '../utils/electron';

export type CallStatus =
  | 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'connected' | 'ended';

export interface CallState {
  status: CallStatus;
  callId: string | null;
  conversationId: string | null;
  peerUserId: string | null;
  peerName: string | null;
  peerAvatarUrl: string | null;
  muted: boolean;
  startedAt: number | null;
  endReason: string | null;
}

const IDLE: CallState = {
  status: 'idle', callId: null, conversationId: null, peerUserId: null,
  peerName: null, peerAvatarUrl: null, muted: false, startedAt: null, endReason: null,
};

/** Auto-end a call that rings unanswered this long (typical no-answer timeout). */
const RING_TIMEOUT_MS = 45_000;

/**
 * Grace period before tearing down a call that bounced to 'disconnected'.
 * WebRTC commonly flaps a healthy connection to 'disconnected' on a brief
 * network blip / Wi-Fi handoff and then recovers to 'connected' on its own.
 * Only tear down if it has NOT recovered (or moved to 'failed') by the time
 * this elapses — so a momentary hiccup can't kill an otherwise-fine call.
 */
const DISCONNECT_GRACE_MS = 6_000;

type SendFn = (frame: Record<string, any>) => void;

interface CallApi {
  call: CallState;
  /** Whether the active call is collapsed to the floating pill. */
  minimized: boolean;
  startCall: (conversationId: string, peerUserId: string, peerName?: string | null, peerAvatarUrl?: string | null) => Promise<void>;
  accept: () => Promise<void>;
  reject: () => void;
  hangup: () => void;
  toggleMute: () => void;
  toggleMinimize: () => void;
  /** Called by the WS layer to deliver an inbound call-* frame. */
  handleSignal: (frame: any) => void;
  /** Called by the WS layer to register the outbound send function. */
  bindSend: (fn: SendFn) => void;
  /** Register the 1:1→group escalation handler (provided by CallEscalationBridge). */
  bindEscalate: (fn: (args: { peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) => void;
}

const Ctx = createContext<CallApi | null>(null);

export function CallProvider({ children }: { children: ReactNode }) {
  const [call, setCall] = useState<CallState>(IDLE);
  const [minimized, setMinimized] = useState(false);
  const [addingOpen, setAddingOpen] = useState(false);
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);
  const escalateRef = useRef<((args: { peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) | null>(null);
  const peerRef = useRef<PeerService | null>(null);
  const sendRef = useRef<SendFn>(() => {});
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  // Detects when the peer is talking → drives the avatar speaking wave.
  const speakingRef = useRef<SpeakingMonitor | null>(null);
  // Pending grace timer for a transient 'disconnected'. Armed when the peer
  // connection flaps to 'disconnected', and cleared the moment it recovers to
  // 'connected', moves to 'failed'/'closed', or the call tears down.
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Buffer for ICE candidates that arrive before the PeerService exists
  // (callee receives candidates before pressing Accept).
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([]);
  const pendingOffer = useRef<RTCSessionDescriptionInit | null>(null);
  // Mirror of the live call routing info for use inside callbacks.
  const routeRef = useRef<{ callId: string; conversationId: string; peerUserId: string } | null>(null);
  // Only the caller applies a remote answer, and only once — guards against a
  // self-call (same account in two windows) or a duplicate frame, which would
  // otherwise throw "setRemoteDescription ... wrong state: stable".
  const isCallerRef = useRef(false);
  const answerAppliedRef = useRef(false);
  // Always-current mirror of call.status for use in unmount/unload handlers
  // whose effect runs only once (stable deps) and would otherwise see a stale
  // status from the closure.
  const statusRef = useRef(call.status);
  statusRef.current = call.status;
  // Cross-tab coordination: the backend fans a call-invite out to EVERY socket
  // the user has open, so each tab rings independently with its own audio state.
  // A reject only goes to the caller, never the user's other tabs — so without
  // this they'd ring on until their own 45s timeout. We broadcast a resolution
  // on this channel so sibling tabs dismiss the same call at once.
  const callChannelRef = useRef<BroadcastChannel | null>(null);
  // call_ids we've already resolved (declined/ended), so a duplicate or
  // re-published call-invite for the same id can't re-ring. Bounded FIFO.
  const declinedCallIdsRef = useRef<string[]>([]);
  // Snapshot of the live call's identity/timing, captured at start/invite and
  // updated on connect, so logCallOutcome() can build a record even as cleanup
  // tears the rest down. Direction is from this side's perspective.
  const callMetaRef = useRef<{
    callId: string; peerUserId: string; conversationId: string;
    direction: 'incoming' | 'outgoing'; ringStartedAt: number; answeredAt: number | null;
  } | null>(null);
  // call_ids already logged by THIS client, so cleanup + the unload beacon
  // can't double-post the same side. Bounded FIFO.
  const loggedCallIdsRef = useRef<string[]>([]);
  const eventLoggedRef = useRef<string[]>([]);

  const bindSend = useCallback((fn: SendFn) => { sendRef.current = fn; }, []);

  const bindEscalate = useCallback((fn: (args: { peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) => {
    escalateRef.current = fn;
  }, []);

  const sendFrame = useCallback((type: string, extra: Record<string, any> = {}) => {
    const r = routeRef.current;
    if (!r) return;
    sendRef.current({
      type, call_id: r.callId, conversation_id: r.conversationId, to_user_id: r.peerUserId, ...extra,
    });
  }, []);

  const logCallOutcome = useCallback((outcome: CallOutcome) => {
    const meta = callMetaRef.current;
    if (!meta) return;
    if (loggedCallIdsRef.current.includes(meta.callId)) return;
    loggedCallIdsRef.current.push(meta.callId);
    if (loggedCallIdsRef.current.length > 50) loggedCallIdsRef.current.shift();
    const endedAt = Date.now();
    const payload: CallLogCreate = {
      call_id: meta.callId,
      peer_user_id: meta.peerUserId,
      conversation_id: meta.conversationId,
      direction: meta.direction,
      call_type: 'voice',
      outcome,
      started_at: new Date(meta.ringStartedAt).toISOString(),
      answered_at: meta.answeredAt ? new Date(meta.answeredAt).toISOString() : null,
      ended_at: new Date(endedAt).toISOString(),
      duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
    };
    void logCall(payload).catch(() => { /* best-effort; peer's row is independent */ });
    if (meta.direction === 'incoming' && outcome === 'missed') bumpMissed();
  }, []);

  const cleanup = useCallback((
    reason: string | null,
    broadcast = true,
    outcome: CallOutcome | 'none' | 'derive' = 'derive',
  ) => {
    // Stop the ring SYNCHRONOUSLY here — cleanup() is the single choke point
    // every teardown (reject/hangup/cancel/end/timeout/connection-lost) passes
    // through. Relying on the status-driven useEffect proved unreliable: on a
    // decline the status went incoming→idle and the overlay closed, but the
    // effect did not always run stopAllCallSounds, so the loop kept ringing
    // until the 45s timeout. A direct call here can never be skipped.
    stopAllCallSounds();
    setCallActive('oneToOne', false);
    // Log this side's perspective exactly once, before the meta is cleared.
    const meta = callMetaRef.current;
    const resolved: CallOutcome | 'none' =
      outcome === 'derive' ? deriveOutcome(reason, meta?.answeredAt != null) : outcome;
    if (resolved !== 'none') logCallOutcome(resolved);
    // Shared in-thread call_event — caller side only (owns the call_id origin).
    // Idempotent server-side on call_id, so a retry/echo is harmless.
    if (meta && isCallerRef.current && resolved !== 'none' && !eventLoggedRef.current.includes(meta.callId)) {
      eventLoggedRef.current.push(meta.callId);
      const endedAt = Date.now();
      void logCallEvent({
        call_id: meta.callId,
        conversation_id: meta.conversationId,
        call_type: 'voice',
        outcome: resolved,
        is_group: false,
        duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
        started_at: new Date(meta.ringStartedAt).toISOString(),
        ended_at: new Date(endedAt).toISOString(),
        peer_user_id: meta.peerUserId,
      }).catch(() => {});
    }
    const resolvedId = routeRef.current?.callId;
    if (resolvedId) {
      // Remember it so a duplicate/re-published invite for this id can't re-ring.
      const seen = declinedCallIdsRef.current;
      if (!seen.includes(resolvedId)) {
        seen.push(resolvedId);
        if (seen.length > 50) seen.shift();
      }
      // Tell this user's OTHER tabs (also ringing this same call) to dismiss it.
      // `broadcast=false` when we're already reacting to a sibling's broadcast.
      if (broadcast) {
        try { callChannelRef.current?.postMessage({ type: 'call-resolved', callId: resolvedId }); } catch { /* noop */ }
      }
    }
    // Kill any pending disconnect grace timer so it can never fire after the
    // call has already been torn down.
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    peerRef.current?.close();
    peerRef.current = null;
    pendingCandidates.current = [];
    pendingOffer.current = null;
    routeRef.current = null;
    callMetaRef.current = null;
    isCallerRef.current = false;
    answerAppliedRef.current = false;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    speakingRef.current?.close();
    speakingRef.current = null;
    setRemoteSpeaking(false);
    setAddingOpen(false);   // never let an open "Add" sheet leak into the next call
    setCall(reason ? { ...IDLE, status: 'ended', endReason: reason } : IDLE);
    if (reason) {
      // brief "ended" flash, then back to idle
      setTimeout(() => { setCall(IDLE); setMinimized(false); }, 1500);
    } else {
      setMinimized(false);
    }
  }, [logCallOutcome]);

  const wirePeer = useCallback((peer: PeerService) => {
    peer.onIceCandidate = (candidate) => sendFrame('ice-candidate', { candidate });
    peer.onRemoteStream = (stream) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
        void remoteAudioRef.current.play().catch(() => {});
      }
      // Watch the peer's loudness so their avatar shows a speaking wave.
      if (!speakingRef.current) {
        speakingRef.current = new SpeakingMonitor((_uid, speaking) => setRemoteSpeaking(speaking));
      }
      speakingRef.current.add('peer', stream);
    };
    peer.onConnectionStateChange = (state) => {
      if (state === 'connected') {
        // Connected (or recovered from a transient blip): cancel any pending
        // disconnect grace timer so it can't tear the live call down.
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        if (callMetaRef.current && callMetaRef.current.answeredAt == null) {
          callMetaRef.current.answeredAt = Date.now();
        }
        setCall((c) => (c.status === 'connected' ? c : { ...c, status: 'connected', startedAt: Date.now() }));
      } else if (state === 'failed' || state === 'closed') {
        // A non-recovering drop — terminal, tear down at once.
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        if (routeRef.current) cleanup('Connection lost');
      } else if (state === 'disconnected') {
        // 'disconnected' is frequently TRANSIENT — a brief network blip or
        // Wi-Fi handoff commonly bounces a healthy call here and it recovers
        // on its own within a few seconds. Don't kill the call immediately;
        // arm a short grace timer instead. A return to 'connected' cancels it
        // above; a move to 'failed'/'closed' tears down at once; only a
        // disconnect that never recovers reaches this callback and tears down.
        // Leave the UI on 'connected' meanwhile so a momentary blip doesn't
        // flicker the call screen.
        if (!disconnectTimerRef.current) {
          disconnectTimerRef.current = setTimeout(() => {
            disconnectTimerRef.current = null;
            if (routeRef.current) cleanup('Connection lost');
          }, DISCONNECT_GRACE_MS);
        }
      }
    };
  }, [sendFrame, cleanup]);

  const flushCandidates = useCallback(async () => {
    const peer = peerRef.current;
    if (!peer) return;
    const queued = pendingCandidates.current;
    pendingCandidates.current = [];
    for (const c of queued) await peer.addIceCandidate(c);
  }, []);

  const startCall = useCallback(async (
    conversationId: string,
    peerUserId: string,
    peerName: string | null = null,
    peerAvatarUrl: string | null = null,
  ) => {
    if (call.status !== 'idle' || isOtherCallActive('oneToOne')) return;
    setCallActive('oneToOne', true);
    const callId = crypto.randomUUID();
    routeRef.current = { callId, conversationId, peerUserId };
    callMetaRef.current = {
      callId, peerUserId, conversationId,
      direction: 'outgoing', ringStartedAt: Date.now(), answeredAt: null,
    };
    isCallerRef.current = true;
    answerAppliedRef.current = false;
    setMinimized(false);
    setCall({ ...IDLE, status: 'outgoing', callId, conversationId, peerUserId, peerName, peerAvatarUrl });
    try {
      const iceServers = await getIceServers();
      const peer = new PeerService(iceServers);
      peerRef.current = peer;
      wirePeer(peer);
      await peer.initLocalAudio();
      const offer = await peer.createOffer();
      sendFrame('call-invite', { sdp: offer, media: 'audio' });
    } catch {
      // Mic blocked/denied or SDP setup failed. Without this, status stays
      // 'outgoing' forever and the ringback loop never stops.
      cleanup('Microphone unavailable');
    }
  }, [call.status, wirePeer, sendFrame, cleanup]);

  const accept = useCallback(async () => {
    if (call.status !== 'incoming' || !routeRef.current || isOtherCallActive('oneToOne')) return;
    // Answering marks this account busy so a concurrent group call is refused.
    setCallActive('oneToOne', true);
    // Stop the ringtone SYNCHRONOUSLY (don't wait on the status effect), and
    // tell sibling tabs ringing this same call to dismiss it — we answered here.
    stopAllCallSounds();
    try { callChannelRef.current?.postMessage({ type: 'call-resolved', callId: routeRef.current.callId }); } catch { /* noop */ }
    setCall((c) => ({ ...c, status: 'connecting' }));
    try {
      const iceServers = await getIceServers();
      const peer = new PeerService(iceServers);
      peerRef.current = peer;
      wirePeer(peer);
      await peer.initLocalAudio();
      if (pendingOffer.current) await peer.setRemoteDescription(pendingOffer.current);
      await flushCandidates();
      const answer = await peer.createAnswer();
      sendFrame('call-answer', { sdp: answer });
    } catch {
      // Mic blocked/denied or SDP setup failed after Accept. Tell the caller
      // and tear down instead of hanging on 'connecting' forever.
      sendFrame('call-end');
      cleanup('Microphone unavailable');
    }
  }, [call.status, wirePeer, sendFrame, flushCandidates, cleanup]);

  const reject = useCallback(() => {
    if (call.status !== 'incoming') return;
    sendFrame('call-reject');
    cleanup(null, true, 'declined');
  }, [call.status, sendFrame, cleanup]);

  const hangup = useCallback(() => {
    if (call.status === 'idle' || call.status === 'ended') return;
    sendFrame(call.status === 'outgoing' ? 'call-cancel' : 'call-end');
    const outcome: CallOutcome =
      call.status === 'outgoing' ? 'cancelled'
        : callMetaRef.current?.answeredAt ? 'completed' : 'failed';
    cleanup(null, true, outcome);
  }, [call.status, sendFrame, cleanup]);

  const toggleMute = useCallback(() => {
    setCall((c) => {
      const muted = !c.muted;
      peerRef.current?.setMuted(muted);
      return { ...c, muted };
    });
  }, []);

  const toggleMinimize = useCallback(() => {
    setMinimized((m) => !m);
  }, []);

  // Drive call sounds straight off the state machine, so loops always start and
  // stop with the call and can never leak. Ringtone (incoming) and ringback
  // (outgoing) loop; connect/end are one-shots. All playback is best-effort.
  useEffect(() => {
    switch (call.status) {
      case 'incoming': startRingtone(); break;
      case 'outgoing': startRingback(); break;
      case 'connected': stopAllCallSounds(); playConnectTone(); break;
      case 'ended': stopAllCallSounds(); playEndTone(); break;
      default: stopAllCallSounds(); break; // idle, connecting
    }
  }, [call.status]);

  // Safety net: stop every loop if the provider unmounts mid-call.
  useEffect(() => () => stopAllCallSounds(), []);

  // Desktop app: ring with the OS ringtone (macOS FaceTime tone / Windows
  // phone ring) instead of the synthesized bell. Best-effort — outside
  // Electron or on any failure the synthesized ringtone stays.
  useEffect(() => {
    let cancelled = false;
    void getSystemRingtone().then((tone) => {
      if (!cancelled && tone) setSystemRingtone(tone.data);
    });
    return () => { cancelled = true; };
  }, []);

  // Surface a ringing call as an always-on-top desktop popup (WhatsApp-style
  // call banner with Accept/Decline), so it's seen — and answerable — while
  // the window is hidden in the tray or behind other apps. The renderer
  // always notifies; MAIN decides visibility (popup suppressed while the
  // window is focused — the CallScreen is the ring UI there — and synced to
  // focus/blur for the whole ring). Withdrawn the moment the call leaves
  // 'incoming' (answered here or in a sibling tab, declined, cancelled,
  // timed out). If it ended UNANSWERED, leave a regular "missed call"
  // notification that deep-links into the conversation.
  const notifiedCallRef = useRef<{
    callId: string; peerName: string | null; conversationId: string | null; avatarUrl: string | null;
  } | null>(null);
  useEffect(() => {
    if (call.status === 'incoming' && call.callId) {
      // One popup per ring — but when the avatar back-fill resolves a photo
      // for the SAME call, notify again: main patches the live popup in
      // place (never re-creates it), so the photo pops in without a flash.
      const avatarUrl = call.peerAvatarUrl ?? null;
      const prev = notifiedCallRef.current;
      if (prev?.callId === call.callId && prev.avatarUrl === avatarUrl) return;
      notifiedCallRef.current = {
        callId: call.callId, peerName: call.peerName, conversationId: call.conversationId, avatarUrl,
      };
      void showCallNotification({
        callId: call.callId,
        title: call.peerName || 'Incoming call',
        body: 'TeamLio audio call',
        iconUrl: avatarUrl,
      });
      return;
    }
    const notified = notifiedCallRef.current;
    if (!notified) return;
    notifiedCallRef.current = null;
    void closeCallNotification(notified.callId);
    // cleanup() reasons for a ring that died unanswered: 45s timeout ('Missed
    // call') or the caller hung up while we rang ('Call cancelled').
    if (call.status === 'ended' && (call.endReason === 'Missed call' || call.endReason === 'Call cancelled')) {
      void showDesktopNotification({
        title: notified.peerName || 'TeamLio',
        body: 'Missed voice call',
        conversationId: notified.conversationId,
      });
    }
  }, [call.status, call.callId, call.peerName, call.peerAvatarUrl, call.conversationId, call.endReason]);

  // Mirror of the sounds safety net: if this renderer goes away mid-ring
  // (provider unmount, reload, auto-update relaunch), withdraw the OS
  // notification too — the fresh renderer has no memory of it and could
  // never close it. Main's TTL is the last-resort backstop.
  useEffect(() => () => {
    if (notifiedCallRef.current) void closeCallNotification(notifiedCallRef.current.callId);
  }, []);

  // Latest accept/reject for the mount-once notification-action subscription
  // below (both callbacks are recreated whenever call.status changes).
  const acceptRef = useRef(accept);
  acceptRef.current = accept;
  const rejectRef = useRef(reject);
  rejectRef.current = reject;

  // React to the notification's buttons: Accept answers right away (main has
  // already focused the window), Decline rejects. A plain body click or
  // dismiss needs nothing here — the ringing CallScreen is the UI.
  useEffect(() => {
    return onCallNotificationAction(({ callId, action }) => {
      if (!callId || routeRef.current?.callId !== callId) return;
      if (statusRef.current !== 'incoming') return;
      if (action === 'accept') void acceptRef.current();
      else if (action === 'decline') rejectRef.current();
    });
  }, []);

  // Hard timeout for the ringing states. Guarantees the ring can NEVER loop
  // forever if the teardown signal is lost — e.g. the caller closed/refreshed
  // their tab (no call-cancel sent) or a frame was dropped. Re-runs on every
  // status change, so it only counts down while actually ringing.
  useEffect(() => {
    if (call.status !== 'incoming' && call.status !== 'outgoing') return;
    const wasOutgoing = call.status === 'outgoing';
    const id = setTimeout(() => {
      if (wasOutgoing) sendFrame('call-cancel');
      cleanup(wasOutgoing ? 'No answer' : 'Missed call');
    }, RING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [call.status, sendFrame, cleanup]);

  // Tell the peer when this tab closes or the provider unmounts mid-call, so
  // their ring / "connecting" UI tears down at once instead of hanging until
  // the timeout above. Best-effort: a synchronous WS send during unload.
  useEffect(() => {
    const notifyPeer = () => {
      const s = statusRef.current;
      if (s === 'idle' || s === 'ended') return;
      // Kill local audio synchronously — don't wait for React's unmount effect,
      // or the ring can keep firing for up to one interval while the tab closes.
      stopAllCallSounds();
      // Withdraw the OS call notification too: after the unload nothing else
      // remembers it (fire-and-forget IPC is fine during unload).
      if (notifiedCallRef.current) void closeCallNotification(notifiedCallRef.current.callId);
      sendFrame(s === 'outgoing' ? 'call-cancel' : s === 'incoming' ? 'call-reject' : 'call-end');
      // Persist THIS side's row even as the tab closes (keepalive fetch
      // survives unload; the normal cleanup POST would be aborted).
      const meta = callMetaRef.current;
      if (meta && !loggedCallIdsRef.current.includes(meta.callId)) {
        loggedCallIdsRef.current.push(meta.callId);
        const outcome: CallOutcome = meta.direction === 'outgoing'
          ? (meta.answeredAt ? 'completed' : 'cancelled')
          : (meta.answeredAt ? 'completed' : 'missed');
        const endedAt = Date.now();
        logCallKeepalive({
          call_id: meta.callId, peer_user_id: meta.peerUserId, conversation_id: meta.conversationId,
          direction: meta.direction, call_type: 'voice', outcome,
          started_at: new Date(meta.ringStartedAt).toISOString(),
          answered_at: meta.answeredAt ? new Date(meta.answeredAt).toISOString() : null,
          ended_at: new Date(endedAt).toISOString(),
          duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
        });
        if (meta.direction === 'incoming' && outcome === 'missed') bumpMissed();
      }
    };
    window.addEventListener('beforeunload', notifyPeer);
    window.addEventListener('pagehide', notifyPeer);
    return () => {
      window.removeEventListener('beforeunload', notifyPeer);
      window.removeEventListener('pagehide', notifyPeer);
    };
  }, [sendFrame]);

  // Cross-tab call resolution. The backend rings EVERY tab the user has open;
  // a reject/accept in one tab is invisible to the others, so they'd ring on
  // until their own timeout. We listen for a sibling's 'call-resolved' and, if
  // THIS tab is merely ringing (incoming) for that same call, dismiss it too.
  // Guarded to 'incoming' so the tab actually handling the call (outgoing /
  // connecting / connected) is never torn down by a sibling — also keeps the
  // same-account two-tab "self-call" test from collapsing the caller tab.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('messenger-call');
    callChannelRef.current = ch;
    ch.onmessage = (e: MessageEvent) => {
      const m = e.data;
      if (
        m?.type === 'call-resolved' && m.callId &&
        routeRef.current?.callId === m.callId &&
        statusRef.current === 'incoming'
      ) {
        cleanup(null, false, 'none'); // false: don't re-broadcast (avoid a ping-pong)
      }
    };
    return () => { ch.close(); callChannelRef.current = null; };
  }, [cleanup]);

  const handleSignal = useCallback((frame: any) => {
    const t = frame?.type;
    if (t === 'call-invite') {
      // Already resolved this call id (declined/ended, possibly in another tab)
      // → ignore a duplicate/re-published invite so it can't re-ring.
      if (frame.call_id && declinedCallIdsRef.current.includes(frame.call_id)) return;
      // Busy: already in a 1:1 call, or in a group call → tell the new caller.
      if (call.status !== 'idle' || isOtherCallActive('oneToOne')) {
        sendRef.current({
          type: 'call-busy', call_id: frame.call_id,
          conversation_id: frame.conversation_id, to_user_id: frame.from_user_id,
        });
        return;
      }
      routeRef.current = {
        callId: frame.call_id, conversationId: frame.conversation_id, peerUserId: frame.from_user_id,
      };
      callMetaRef.current = {
        callId: frame.call_id, peerUserId: frame.from_user_id, conversationId: frame.conversation_id,
        direction: 'incoming', ringStartedAt: Date.now(), answeredAt: null,
      };
      isCallerRef.current = false;
      answerAppliedRef.current = false;
      pendingOffer.current = frame.sdp ?? null;
      setMinimized(false);
      setCall({
        ...IDLE, status: 'incoming', callId: frame.call_id, conversationId: frame.conversation_id,
        peerUserId: frame.from_user_id, peerName: frame.from_name ?? frame.from_user_id,
        peerAvatarUrl: frame.from_avatar_url ?? null,
      });
      // If the invite carried no caller photo, resolve it from the conversation
      // (its members carry effective avatars — same source the sidebar uses) and
      // patch it in. Works against the current backend without a deploy.
      if (!frame.from_avatar_url && frame.conversation_id) {
        const callId = frame.call_id;
        const peerId = frame.from_user_id;
        void getConversation(frame.conversation_id).then((conv: any) => {
          const peer = conv?.members?.find((m: any) => m.id === peerId);
          const url = peer?.avatar_url || (conv?.type !== 'dm' ? conv?.avatar_url : null) || null;
          if (url) setCall((c) => (c.callId === callId ? { ...c, peerAvatarUrl: url } : c));
        }).catch(() => { /* best-effort */ });
      }
      return;
    }
    // Ignore frames that don't belong to the current call.
    if (!routeRef.current || frame.call_id !== routeRef.current.callId) return;

    if (t === 'call-answer') {
      if (!isCallerRef.current) {
        // Echo of THIS user answering in another session (the backend fans
        // the answer back to the answerer's other sockets): if we're a
        // still-ringing callee tab, dismiss silently — answered elsewhere,
        // not missed. The session that actually answered is past 'incoming'
        // and ignores its own echo here.
        if (statusRef.current === 'incoming') cleanup(null, true, 'none');
        return;
      }
      // Only the caller applies an answer, and only once (see refs above).
      if (answerAppliedRef.current) return;
      answerAppliedRef.current = true;
      // Stop the ringback SYNCHRONOUSLY — like every teardown path, don't rely
      // on the status effect (the same unreliability cleanup() was hardened for).
      stopAllCallSounds();
      void peerRef.current?.setRemoteDescription(frame.sdp).then(flushCandidates);
      setCall((c) => (c.status === 'outgoing' ? { ...c, status: 'connecting' } : c));
    } else if (t === 'ice-candidate') {
      if (peerRef.current) void peerRef.current.addIceCandidate(frame.candidate);
      else pendingCandidates.current.push(frame.candidate);
    } else if (t === 'call-reject') {
      cleanup('Call declined');
    } else if (t === 'call-busy') {
      cleanup('User busy');
    } else if (t === 'call-cancel') {
      cleanup('Call cancelled');
    } else if (t === 'call-end') {
      cleanup('Call ended');
    }
  }, [call.status, flushCandidates, cleanup]);

  const apiValue: CallApi = {
    call, minimized, startCall, accept, reject, hangup, toggleMute, toggleMinimize, handleSignal, bindSend, bindEscalate,
  };

  return (
    <Ctx.Provider value={apiValue}>
      {children}
      {call.status !== 'idle' && (
        <CallScreen
          status={call.status}
          peerName={call.peerName}
          peerAvatarUrl={call.peerAvatarUrl}
          muted={call.muted}
          startedAt={call.startedAt}
          endReason={call.endReason}
          minimized={minimized}
          remoteSpeaking={remoteSpeaking}
          onAccept={accept}
          onReject={reject}
          onToggleMute={toggleMute}
          onHangup={hangup}
          onToggleMinimize={toggleMinimize}
          onAddParticipant={() => setAddingOpen(true)}
        />
      )}
      {addingOpen && call.peerUserId && (
        <StartCallPicker
          multi
          title="Add to call"
          excludeUserIds={[call.peerUserId]}
          onClose={() => setAddingOpen(false)}
          onPickMany={(users) => {
            setAddingOpen(false);
            escalateRef.current?.({ peerUserId: call.peerUserId!, peerName: call.peerName, newUserIds: users.map(u => u.user_id) });
          }}
        />
      )}
      {/* Remote audio sink — hidden, autoplay. */}
      <audio ref={remoteAudioRef} autoPlay />
    </Ctx.Provider>
  );
}

export function useCall(): CallApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCall must be used within a CallProvider');
  return ctx;
}
